import React from 'react';
import styled from 'styled-components';
import {Icon} from './Icon.tsx';
import {ICON_ARROW_DOWN, ICON_ARROW_UP} from '../refs/Icons.tsx';
import {getColor} from '../utils/styled.ts';
import {LOCHMARA, LOCHMARA_SHADOW, TOREA_BAY} from '../refs/Colors.tsx';
import {Toggleable} from "@/types/Toggleable.ts";

type AccordionProps = {
  className?: string;
  toggleable: Toggleable;
  label: string;
  children?: React.ReactNode;
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  color: ${getColor(LOCHMARA_SHADOW)};
  cursor: pointer;

  &:hover {
    color: ${getColor(LOCHMARA)};
  }

  &:active {
    color: ${getColor(TOREA_BAY)};
  }
`;

const Label = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-weight: 600;
  font-size: 13px;
  line-height: 1;
  margin: 0;
  color: inherit;
`;

const ChevronIcon = styled(Icon)`
  color: inherit;
`;

const Content = styled.div`
  overflow: hidden;
  transition-property: max-height, padding-top, padding-bottom, opacity;
  transition-duration: 0.4s, 0.4s, 0.4s, 0s;
  transition-delay: 0s, 0s, 0s, 0s;

  &.is-closed {
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
    opacity: 0;
    transition-delay: 0s, 0s, 0s, 0.4s;
    transition-timing-function: ease-out;
  }

  &.is-open {
    max-height: 100vh;
    opacity: 1;
    transition-timing-function: ease-in;
  }
`;

export const Accordion: React.FC<AccordionProps> = ({
  className,
  toggleable,
  label,
  children,
}) => {
  return (
    <Wrapper className={className}>
      <Header className="accordion-header" onClick={toggleable.toggle}>
        <Label>{label}</Label>
        <ChevronIcon icon={toggleable.isOpen ? ICON_ARROW_UP : ICON_ARROW_DOWN} size={16} />
      </Header>
      <Content className={ toggleable.isOpen ? "is-open" : "is-closed" }>{children}</Content>
    </Wrapper>
  );
};

export default Accordion;
